import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControl, FormHelperText, Link, Select } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { useBuildRecognizeURL } from '@bonusly/url';
import { Permission, ProtectedContent, usePermission } from '@bonusly/who-am-i';

import { FormLabel } from '../../form/form-label';
import { useGetIncentives } from '../hooks/use-incentives';
import { Incentive } from '../types/announcements';

export interface SelectIncentivesDropdownProps {
	register: any;
	recipients?: Recipient[];
	value?: string;
}

export const SelectIncentivesDropdown: FC<SelectIncentivesDropdownProps> = ({ register, recipients, value }) => {
	const { t } = useTranslation('announcements');
	const canAccessIncentivesExperience = usePermission(Permission.CanAccessIncentivesExperience);
	const { data: companyIncentives } = useGetIncentives({ recipients });
	const companyHasIncentives = companyIncentives && companyIncentives.length > 0;
	const recognizeURL = useBuildRecognizeURL();

	if (!canAccessIncentivesExperience) {
		return <input type="hidden" {...register('claimable_award_id')} />;
	}

	return (
		<FormControl>
			<FormLabel fontSize={50} isOptional>
				{t('modal.incentive_label')}
			</FormLabel>
			<Select
				placeholder={t('modal.select_incentive')}
				borderColor="border.input"
				isDisabled={!companyHasIncentives}
				data-testid="incentive-input"
				fontSize={50}
				_hover={{ borderColor: 'border.input' }}
				_focusVisible={{ borderColor: 'border.input' }}
				value={value}
				{...register('claimable_award_id')}
			>
				{companyHasIncentives &&
					companyIncentives.map((incentive: Incentive) => {
						return (
							<option key={incentive.id} value={incentive.id}>
								{incentive.name}
							</option>
						);
					})}
			</Select>
			<ProtectedContent permissions={Permission.CanManageAwards}>
				<FormHelperText as="span">{t('modal.incentive_helper_1')}</FormHelperText>
				<Link
					color="text.interactive"
					display="inline-block"
					fontFamily="ui"
					fontWeight="normal"
					fontSize={50}
					data-testid="create-incentive-link"
					mt={1}
					px={1}
					aria-label={t('modal.create_incentive')}
					_hover={{ textDecoration: 'none' }}
					href={recognizeURL('/admin/awards#claimable')}
					target="_blank"
				>
					{t('modal.incentive_helper_link')}
				</Link>
				<FormHelperText as="span">{t('modal.incentive_helper_2')}</FormHelperText>
			</ProtectedContent>
		</FormControl>
	);
};
export default SelectIncentivesDropdown;
